import { PageCtxConsumer } from "$root/components/PageCtx";
import { formatTime } from "$root/localization/formatters";
import { seconds } from "$root/utils";
import { TaskQueueStatusIndicator, type TaskQueueState } from "$root/utils/task-queue";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultTabTitle, Tab, TabGroup } from "@mdotm/shadcn/legacy/components";
import { useDebounced } from "@mdotm/shadcn/legacy/react-extensions";
import type { Atom } from "@mdotm/shadcn/legacy/reactivity";
import { switchExpr } from "@mdotm/shadcn/legacy/utils";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
	type Control,
	type FormState,
	type UseFormSetValue,
	useForm,
	useWatch,
	type UseFormGetValues,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useUserValue } from "../user";
import { MultipleNotificationBox } from "./boxes/MultipleNotificationBox";
import { NotificationBox } from "./boxes/NotificationBox";
import { ConstraintBreachIcon } from "./icons/ConstraintBreachIcon";
import { ConstraintBreachWarningIcon } from "./icons/ConstraintBreachWarningIcon";
import { InstrumentChangeIcon } from "./icons/InstrumentChangeIcon";
import { MarketChangeIcon } from "./icons/MarketChangeIcon";
import { NewMarketChangeIcon } from "./icons/NewMarketChangeIcon";
import { PositioningChangeIcon } from "./icons/PositioningChangeIcon";
import { ShareEntityIcon } from "./icons/ShareEntityIcon";
import { StatusChangeIcon } from "./icons/StatusChangeIcon";
import type { NotificationMap } from "./map";

const PortfolioEvents = ({
	control,
	formState,
	setValue,
	getValues,
}: {
	control: Control<NotificationMap, any>;
	formState: FormState<NotificationMap>;
	setValue: UseFormSetValue<NotificationMap>;
	getValues: UseFormGetValues<NotificationMap>;
}) => {
	const { t } = useTranslation();
	return (
		<div className="grid gap-4 mb-8">
			<NotificationBox
				infoGraph={<StatusChangeIcon />}
				checkbox={{
					children: "Status change",
					name: "PortfolioStatusUpdateAlertCheck.checked",
					value: useWatch({ control, name: "PortfolioStatusUpdateAlertCheck.checked" }),
					control,
					formState,
					onChange(newValue) {
						setValue("UniverseStatusUpdateAlertCheck.checked", newValue, { shouldDirty: true });
					},
				}}
			>
				<p className="mb-2 text-sm">
					{t("NOTIFICATION_SETTINGS.PORTFOLIO_EVENTS.STATUS_CHANGE.UPDATE_DESCRIPTION.TITLE")}
				</p>
				<ul className="list-disc pl-8 text-sm">
					{t("NOTIFICATION_SETTINGS.PORTFOLIO_EVENTS.STATUS_CHANGE.UPDATE_DESCRIPTION.VALUES", {
						returnObjects: true,
					}).map((value) => (
						<li key={value}>{value}</li>
					))}
				</ul>
			</NotificationBox>
			<div className={`h-px w-full bg-[var(--palette-N200)]`} />
			<MultipleNotificationBox
				infoGraph={<ConstraintBreachIcon />}
				label="Constraint breach"
				onChange={(newValue) => {
					setValue("PortfolioConstraintsAlertCheck.checked", newValue, { shouldDirty: true });
					setValue("RiskConstraintAlertCheck.checked", newValue, { shouldDirty: true });
					setValue("PortfolioStrategyBreachedAlertCheck.checked", newValue, { shouldDirty: true });
				}}
				checkboxes={[
					{
						children: "Portfolio constraints",
						name: "PortfolioConstraintsAlertCheck.checked",
						value: useWatch({ control, name: "PortfolioConstraintsAlertCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Risk constraints",
						name: "RiskConstraintAlertCheck.checked",
						value: useWatch({ control, name: "RiskConstraintAlertCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Operative constraints",
						name: "PortfolioStrategyBreachedAlertCheck.checked",
						value: useWatch({ control, name: "PortfolioStrategyBreachedAlertCheck.checked" }),
						control,
						formState,
					},
				]}
			>
				<p className="mb-2 text-sm">A notification will be sent when one or more constraints have been breached</p>
			</MultipleNotificationBox>
			<div className={`h-px w-full bg-[var(--palette-N200)]`} />
			<MultipleNotificationBox
				infoGraph={<ConstraintBreachWarningIcon />}
				label="Constraint breach - early warning"
				onChange={(newValue) => {
					setValue("PortfolioConstraintWarningAlertCheck.checked", newValue, { shouldDirty: true });
					setValue("RiskConstraintWarningAlertCheck.checked", newValue, { shouldDirty: true });
					setValue("PortfolioStrategyWarningAlertCheck.checked", newValue, { shouldDirty: true });
				}}
				checkboxes={[
					{
						children: "Portfolio constraints",
						name: "PortfolioConstraintWarningAlertCheck.checked",
						value: useWatch({ control, name: "PortfolioConstraintWarningAlertCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Risk constraints",
						name: "RiskConstraintWarningAlertCheck.checked",
						value: useWatch({ control, name: "RiskConstraintWarningAlertCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Operative constraints",
						name: "PortfolioStrategyWarningAlertCheck.checked",
						value: useWatch({ control, name: "PortfolioStrategyWarningAlertCheck.checked" }),
						control,
						formState,
					},
				]}
			>
				<p className="mb-2 text-sm">
					A notification will be sent when one or more constraints is close to be breached.{" "}
				</p>
			</MultipleNotificationBox>
			<div className={`h-px w-full bg-[var(--palette-N200)]`} />{" "}
			<MultipleNotificationBox
				infoGraph={<ConstraintBreachIcon />}
				label="Constraint Breached - Send to Execution"
				onChange={(newValue) => {
					setValue("PortfolioConstraintOnProposalExecutionCheck.checked", newValue, { shouldDirty: true });
					setValue("PortfolioStrategyConstraintOnProposalExecutionCheck.checked", newValue, { shouldDirty: true });
					setValue("RiskConstraintOnProposalExecutionCheck.checked", newValue, { shouldDirty: true });
					setValue("ConstraintBreachOnProposalExecutionCheck.checked", newValue, { shouldDirty: true });
				}}
				checkboxes={[
					{
						children: "Portfolio constraints",
						name: "PortfolioConstraintOnProposalExecutionCheck.checked",
						value: useWatch({ control, name: "PortfolioConstraintOnProposalExecutionCheck.checked" }),
						control,
						formState,
						onChange(portfolioConstraintOnProposalExecutionCheck) {
							const formValues = getValues();
							const someExecutionAlertAreEnabled =
								formValues.RiskConstraintOnProposalExecutionCheck.checked ||
								formValues.PortfolioStrategyConstraintOnProposalExecutionCheck.checked ||
								portfolioConstraintOnProposalExecutionCheck;

							setValue("ConstraintBreachOnProposalExecutionCheck.checked", someExecutionAlertAreEnabled, {
								shouldDirty:
									someExecutionAlertAreEnabled !== formValues.ConstraintBreachOnProposalExecutionCheck.checked,
							});
						},
					},
					{
						children: "Risk constraints",
						name: "RiskConstraintOnProposalExecutionCheck.checked",
						value: useWatch({ control, name: "RiskConstraintOnProposalExecutionCheck.checked" }),
						control,
						formState,
						onChange(RiskConstraintOnProposalExecutionCheck) {
							const formValues = getValues();
							const someExecutionAlertAreEnabled =
								formValues.PortfolioConstraintOnProposalExecutionCheck.checked ||
								formValues.PortfolioStrategyConstraintOnProposalExecutionCheck.checked ||
								RiskConstraintOnProposalExecutionCheck;

							setValue("ConstraintBreachOnProposalExecutionCheck.checked", someExecutionAlertAreEnabled, {
								shouldDirty:
									someExecutionAlertAreEnabled !== formValues.ConstraintBreachOnProposalExecutionCheck.checked,
							});
						},
					},
					{
						children: "Operative constraints",
						name: "PortfolioStrategyConstraintOnProposalExecutionCheck.checked",
						value: useWatch({ control, name: "PortfolioStrategyConstraintOnProposalExecutionCheck.checked" }),
						control,
						formState,
						onChange(PortfolioStrategyConstraintOnProposalExecutionCheck) {
							const formValues = getValues();
							const someExecutionAlertAreEnabled =
								formValues.PortfolioConstraintOnProposalExecutionCheck.checked ||
								formValues.RiskConstraintOnProposalExecutionCheck.checked ||
								PortfolioStrategyConstraintOnProposalExecutionCheck;

							setValue("ConstraintBreachOnProposalExecutionCheck.checked", someExecutionAlertAreEnabled, {
								shouldDirty:
									someExecutionAlertAreEnabled !== formValues.ConstraintBreachOnProposalExecutionCheck.checked,
							});
						},
					},
				]}
			>
				<p className="mb-2 text-sm">
					A notification will be sent when a portfolio proposal is executed with breached constraints.
				</p>
			</MultipleNotificationBox>
			<div className={`h-px w-full bg-[var(--palette-N200)]`} />
			<NotificationBox
				infoGraph={<InstrumentChangeIcon />}
				checkbox={{
					children: "Instrument changes",
					name: "InstrumentsChangesChildAlertCheck.checked",
					value: useWatch({ control, name: "InstrumentsChangesChildAlertCheck.checked" }),
					control,
					formState,
				}}
			>
				<p className="mb-2 text-sm">
					A notification will be sent when an instrument present in your account has changed, including:{" "}
				</p>
				<ul className="list-disc pl-8 text-sm">
					<li>ISIN delisted</li>
					<li>ISIN changed</li>
				</ul>
			</NotificationBox>
			<div className={`h-px w-full bg-[var(--palette-N200)]`} />
			<NotificationBox
				infoGraph={<NewMarketChangeIcon />}
				checkbox={{
					children: "Market scenario changes",
					name: "MarketViewExpiredAlertCheck.checked",
					value: useWatch({ control, name: "MarketViewExpiredAlertCheck.checked" }),
					control,
					formState,
				}}
			>
				<p className="mb-2 text-sm">A notification will be sent when your market view forecast horizon expires.</p>
			</NotificationBox>
		</div>
	);
};

const MarketEvents = ({
	control,
	formState,
	setValue,
}: {
	control: Control<NotificationMap, any>;
	formState: FormState<NotificationMap>;
	setValue: UseFormSetValue<NotificationMap>;
}) => {
	return (
		<div className="grid gap-4 mb-8">
			<MultipleNotificationBox
				infoGraph={<PositioningChangeIcon />}
				label="Positioning changes"
				onChange={(newValue) => {
					setValue("EquityMarketPositioningAlertCheck.checked", newValue, { shouldDirty: true });
					setValue("FixedIncomeMarketPositioningAlertCheck.checked", newValue, { shouldDirty: true });
					setValue("CommoditiesMarketPositioningAlertCheck.checked", newValue, { shouldDirty: true });
				}}
				checkboxes={[
					{
						children: "Equity",
						name: "EquityMarketPositioningAlertCheck.checked",
						value: useWatch({ control, name: "EquityMarketPositioningAlertCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Fixed income",
						name: "FixedIncomeMarketPositioningAlertCheck.checked",
						value: useWatch({ control, name: "FixedIncomeMarketPositioningAlertCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Commodities",
						name: "CommoditiesMarketPositioningAlertCheck.checked",
						value: useWatch({ control, name: "CommoditiesMarketPositioningAlertCheck.checked" }),
						control,
						formState,
					},
				]}
			>
				<p className="mb-2 text-sm">
					Notify me every time Sphere detects a regime shift on the macro and micro asset class. Please select the macro
					asset classes you want to monitoring
				</p>
			</MultipleNotificationBox>
			<div className={`h-px w-full bg-[var(--palette-N200)]`} />
			<MultipleNotificationBox
				infoGraph={<MarketChangeIcon />}
				label="Asset Classes regime changes"
				onChange={(newValue) => {
					setValue("EquityMarketRegimeChangesAlertCheck.checked", newValue, { shouldDirty: true });
					setValue("FixedIncomeMarketRegimeChangesAlertCheck.checked", newValue, { shouldDirty: true });
					setValue("CommoditiesMarketRegimeChangesAlertCheck.checked", newValue, { shouldDirty: true });
				}}
				checkboxes={[
					{
						children: "Equity",
						name: "EquityMarketRegimeChangesAlertCheck.checked",
						value: useWatch({ control, name: "EquityMarketRegimeChangesAlertCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Fixed income",
						name: "FixedIncomeMarketRegimeChangesAlertCheck.checked",
						value: useWatch({ control, name: "FixedIncomeMarketRegimeChangesAlertCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Commodities",
						name: "CommoditiesMarketRegimeChangesAlertCheck.checked",
						value: useWatch({ control, name: "CommoditiesMarketRegimeChangesAlertCheck.checked" }),
						control,
						formState,
					},
				]}
			>
				<p className="mb-2 text-sm">
					Notify me every time Sphere detects a macro or micro asset class shift on the suggested positioning. Please
					select the macro asset classes you want to monitor.
				</p>
			</MultipleNotificationBox>
		</div>
	);
};

const SharingEvents = ({
	control,
	formState,
	setValue,
}: {
	control: Control<NotificationMap, any>;
	formState: FormState<NotificationMap>;
	setValue: UseFormSetValue<NotificationMap>;
}) => {
	return (
		<div className="grid gap-4 mb-8">
			<MultipleNotificationBox
				infoGraph={<ShareEntityIcon />}
				label="Inform me when:"
				onChange={(newValue) => {
					setValue("SharingEntityEmailOnEditCheck.checked", newValue, { shouldDirty: true });
					setValue("SharingEntityEmailOnShareCheck.checked", newValue, { shouldDirty: true });
					setValue("SharingEntityEmailOnOwnerPassedCheck.checked", newValue, { shouldDirty: true });
					setValue("SharingEntityEmailOnEditForSharedCheck.checked", newValue, { shouldDirty: true });
				}}
				checkboxes={[
					{
						children: "A portfolio studio entity is shared with me",
						name: "SharingEntityEmailOnShareCheck.checked",
						value: useWatch({ control, name: "SharingEntityEmailOnShareCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Someone modifies a portfolio studio entity that I own",
						name: "SharingEntityEmailOnEditCheck.checked",
						value: useWatch({ control, name: "SharingEntityEmailOnEditCheck.checked" }),
						control,
						formState,
					},
					{
						children: "The ownership of a portfolio studio entity is transferred to me",
						name: "SharingEntityEmailOnOwnerPassedCheck.checked",
						value: useWatch({ control, name: "SharingEntityEmailOnOwnerPassedCheck.checked" }),
						control,
						formState,
					},
					{
						children: "Someone modifies a portfolio studio entity shared with me",
						name: "SharingEntityEmailOnEditForSharedCheck.checked",
						value: useWatch({ control, name: "SharingEntityEmailOnEditForSharedCheck.checked" }),
						control,
						formState,
					},
				]}
			/>
		</div>
	);
};

type NotificationSettingsProps = {
	notifications: NotificationMap;
	onSettingChange(items: NotificationMap): void;
	updateQueueState$: Atom<TaskQueueState>;
};

const NotificationSettings = (props: NotificationSettingsProps): React.JSX.Element => {
	const user = useUserValue();

	const TABS = useMemo(() => {
		const services: Array<"Portfolio events" | "Market events" | "Sharing options"> = [];
		if (user.availableServices.includes("INVESTMENTS")) {
			services.push("Portfolio events");
		}
		if (user.availableServices.includes("REPORTS")) {
			services.push("Market events");
		}
		services.push("Sharing options");
		return services;
	}, [user.availableServices]);

	const [currentTab, setCurrentTab] = useState<number>(0);

	const { control, reset, setValue, formState, handleSubmit, getValues } = useForm<NotificationMap>({
		defaultValues: props.notifications,
		resolver: resolver,
	});

	const debouncedSave = useDebounced(
		() => {
			if (!formState.isDirty) {
				return;
			}
			handleSubmit((values) => {
				props.onSettingChange(values);
				reset(values, { keepDirtyValues: false });
			}, console.log)();
		},
		{ debounceInterval: seconds(0.5), unmountBehavior: "flush" },
	);

	const debouncedSaveInvokeRef = useUnsafeUpdatedRef(debouncedSave.invoke);

	useEffect(() => {
		if (formState.isDirty) {
			debouncedSaveInvokeRef.current();
		}
	}, [formState.isDirty, debouncedSaveInvokeRef]);

	return (
		<>
			<PageCtxConsumer>
				{(ctx) =>
					ctx.titleActionPortalTarget &&
					createPortal(
						<TaskQueueStatusIndicator
							taskQueueState$={props.updateQueueState$}
							labels={{
								running: "Saving...",
								success: (date) => `Saved at ${formatTime(date)}`,
								error: "Unable to save",
							}}
						/>,
						ctx.titleActionPortalTarget,
					)
				}
			</PageCtxConsumer>
			<TabGroup palette="primary" tabIndex={currentTab} onTabChange={setCurrentTab}>
				{TABS.map((tabName) => (
					<Tab
						className="rounded-sm bg-white px-4 py-6 overflow-hidden"
						key={tabName}
						title={(titleProps) => {
							return (
								<DefaultTabTitle
									{...titleProps}
									iconSize={16}
									icon={switchExpr(tabName, {
										"Market events": () => "News-category-Market-view-aligment" as const,
										"Portfolio events": () => "Portfolio" as const,
										"Sharing options": () => "share" as const,
									})}
								>
									{tabName}
								</DefaultTabTitle>
							);
						}}
					>
						{TABS.indexOf("Portfolio events") === currentTab && (
							<PortfolioEvents control={control} formState={formState} setValue={setValue} getValues={getValues} />
						)}
						{TABS.indexOf("Market events") === currentTab && (
							<MarketEvents control={control} formState={formState} setValue={setValue} />
						)}
						{TABS.indexOf("Sharing options") === currentTab && (
							<SharingEvents control={control} formState={formState} setValue={setValue} />
						)}
					</Tab>
				))}
			</TabGroup>
		</>
	);
};

const common = z.object({
	checked: z.boolean(),
	type: z.string(),
});

const validation = {
	PortfolioStatusUpdateAlertCheck: common,
	UniverseStatusUpdateAlertCheck: common,

	PortfolioConstraintsAlertCheck: common,
	RiskConstraintAlertCheck: common,
	PortfolioStrategyBreachedAlertCheck: common,
	PortfolioConstraintWarningAlertCheck: common,
	RiskConstraintWarningAlertCheck: common,
	PortfolioStrategyWarningAlertCheck: common,

	ConstraintBreachOnProposalExecutionCheck: common,
	PortfolioConstraintOnProposalExecutionCheck: common,
	PortfolioStrategyConstraintOnProposalExecutionCheck: common,
	RiskConstraintOnProposalExecutionCheck: common,

	InstrumentsChangesChildAlertCheck: common,
	MarketViewExpiredAlertCheck: common,
	// GrowthMarketViewAlignmentAlertCheck: common.merge(
	// 	z.object({ threshold: z.number({ invalid_type_error: "Insert a valid value" }) }),
	// ),
	// LateralPhaseMarketViewAlignmentAlertCheck: common.merge(
	// 	z.object({ threshold: z.number({ invalid_type_error: "Insert a valid value" }) }),
	// ),
	// HighStressMarketViewAlignmentAlertCheck: common.merge(
	// 	z.object({ threshold: z.number({ invalid_type_error: "Insert a valid value" }) }),
	// ),
	EquityMarketPositioningAlertCheck: common,
	FixedIncomeMarketPositioningAlertCheck: common,
	CommoditiesMarketPositioningAlertCheck: common,
	EquityMarketRegimeChangesAlertCheck: common,
	FixedIncomeMarketRegimeChangesAlertCheck: common,
	CommoditiesMarketRegimeChangesAlertCheck: common,
	SharingEntityEmailOnEditCheck: common,
	SharingEntityEmailOnOwnerPassedCheck: common,
	SharingEntityEmailOnShareCheck: common,
	SharingEntityEmailOnEditForSharedCheck: common,
} satisfies Record<keyof NotificationMap, unknown>;

const resolver = zodResolver(z.object(validation));

export default NotificationSettings;
